import type { Product, ProductColor, ProductImageType } from 'product/types'
import type { ProductPrices } from 'product/types/Prices'
import type { ProductLocations } from 'product/types/ProductLocations'
import { createStore } from 'zustand/vanilla'

export type ProductCardInitialState = {
	readonly product: Product
	readonly lookId: string
	readonly dataTestId: string
	readonly defaultColorId: string
	readonly location: ProductLocations
	readonly featured: boolean
	readonly imageSortCriteria: ProductImageType[]
	readonly priority?: boolean
	readonly productListId?: string
	readonly noDraggable?: boolean
	readonly showDiscount?: boolean
	readonly prices?: ProductPrices // This property allows us to manage the call for product pricing from the client or server as needed.
	/** On add to cart callback */
	readonly onAddToCart?: () => void
	readonly onImageClick?: () => void
}

export type ProductCardState = {
	product: Product
	productId: Product['id']
	productColors: ProductColor[]
	initialProductId: string
	dataTestId: string
	lookId: string
	defaultColorId: string
	selectedColorId: ProductColor['id']
	location: ProductLocations
	featured: boolean
	imageSortCriteria: ProductImageType[]
	priority?: boolean
	productListId?: string
	draggable?: boolean
	showDiscount?: boolean
	prices?: ProductPrices
}

export type ProductCardActions = {
	setProductId: (productId: string) => void
	setProduct: (product: Product) => void
	setSelectedColorId: (colorId: string) => void
	/** On add to cart callback */
	onAddToCart?: () => void
	onImageClick?: () => void
}

export type ProductCardStore = ProductCardState & ProductCardActions

export const createProductCardStore = (initState: ProductCardInitialState) =>
	createStore<ProductCardStore>()((set) => ({
		...initState,
		productId: initState.product.id,
		productColors: initState.product.colors,
		initialProductId: initState.product.id,
		selectedColorId: initState.defaultColorId,
		setProductId: (productId) => set({ productId }),
		setProduct: (product) => set({ product, productId: product.id }),
		setSelectedColorId: (selectedColorId) => set({ selectedColorId }),
	}))
